// Read-only preview of required-field validation. This is what the admin
// sees right before clicking "Publish" — and it's the same rule set the
// backend enforces (fetched via GET /dealers/:id/validation).
import React, { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { dealersApi } from '../../../api';

export default function ReviewSection({ dealerId }) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await dealersApi.validation(dealerId);
        if (alive) setResult(r);
      } catch (err) {
        if (alive) setError(err.message || 'Failed to validate');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [dealerId]);

  if (loading) return <div className="text-sm text-muted-foreground">Checking required fields…</div>;
  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!result) return null;

  if (result.valid) {
    return (
      <div className="rounded-md border border-green-500/40 bg-green-500/10 p-4 flex items-start gap-3">
        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
        <div>
          <div className="font-medium text-green-700">Ready to publish</div>
          <div className="text-xs text-muted-foreground">
            Dealer type: <code>{result.dealer_type}</code>. All required fields are set.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 p-4 space-y-2">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
        <div>
          <div className="font-medium text-yellow-700">Can't publish yet</div>
          <div className="text-xs text-muted-foreground">
            Fix these before publish. Saving drafts still works.
          </div>
        </div>
      </div>
      <ul className="list-disc list-inside text-sm text-yellow-800 pl-6">
        {result.errors.map((e, i) => <li key={i}>{e}</li>)}
      </ul>
    </div>
  );
}
